import { useCallback, useContext, useState } from "react"
import { UserContext } from "../../../context/userContext"
import { gql, useLazyQuery } from "@apollo/client"
import { AutoComplete } from "primereact/autocomplete"
import { Tooltip } from "primereact/tooltip"

import styles from "../../../styles/StaffSelector.module.css"
import { classNames } from "primereact/utils"
import {
  ORGANIZATION_SUPERVISOR_ROLE,
  SYSTEM_ADMIN_ROLE,
} from "../../../helpers/staffRoleHelpers"

// GraphQL query definition for paginated search
const STAFF_QUERY = gql`
  query SearchStaffPaginated(
    $organization_id: ID!
    $search: String
    $limit: Int
    $offset: Int
    $sortField: String
    $sortOrder: String
  ) {
    searchStaffPaginated(
      organization_id: $organization_id
      search: $search
      limit: $limit
      offset: $offset
      sortField: $sortField
      sortOrder: $sortOrder
    ) {
      staff {
        staff_membership_id
        staff_name_first
        staff_name_last
        staff_full_name
        staff_status
        staff_role_id
        organization_name
        value
        label
      }
      pagination {
        total
        limit
        offset
        hasMore
      }
    }
  }
`

export default function StaffSelector() {
  const userCtx = useContext(UserContext)
  const [selectedStaff, setSelectedStaff] = useState("")
  const [staffOptions, setStaffOptions] = useState([])
  const currentStaffRole = parseInt(userCtx.session.user.staff_role, 10)

  const [fetchStaff] = useLazyQuery(STAFF_QUERY, {
    fetchPolicy: "cache-and-network",
  })

  const searchStaff = useCallback(
    async (event) => {
      if (!event.query || event.query.length >= 3) {
        try {
          const result = await fetchStaff({
            variables: {
              organization_id: userCtx.session.user.organization_id,
              search: event.query,
              limit: 100,
              offset: 0,
              sortField: "full_name",
              sortOrder: "asc",
            },
          })

          if (Number.isNaN(currentStaffRole)) {
            setStaffOptions([])
            return
          }

          const staffResults = result?.data?.searchStaffPaginated?.staff || []

          // Do not show staff with a higher role than the current user.
          const filteredStaffResults = staffResults.filter((staff) => {
            const candidateRole = parseInt(staff.staff_role_id, 10)

            if (Number.isNaN(candidateRole)) {
              return false
            }

            return candidateRole >= currentStaffRole
          })

          setStaffOptions(filteredStaffResults)
        } catch (error) {
          console.error("Error fetching staff:", error)
          setStaffOptions([])
        }
      } else {
        setStaffOptions([])
      }
    },
    [fetchStaff, userCtx.session.user.organization_id, currentStaffRole],
  )

  const selectStaff = useCallback(
    async (e) => {
      // Update the current staff membership in the user context
      await userCtx.changeCurrentStaffMembershipId(e.value.value)

      // Reset selection and options after selection
      setSelectedStaff("")
      setStaffOptions([])
    },
    [userCtx],
  )

  return (
    <div>
      {!Number.isNaN(currentStaffRole) &&
        currentStaffRole >= SYSTEM_ADMIN_ROLE &&
        currentStaffRole <= ORGANIZATION_SUPERVISOR_ROLE && (
          <>
            <h2>Active Staff Members</h2>
            <div className={styles.staffSelectorInputContainer}>
              <AutoComplete
                className={styles.staffSelector}
                value={selectedStaff}
                placeholder="Search Staff..."
                field="staff_full_name"
                suggestions={staffOptions}
                completeMethod={searchStaff}
                onChange={(e) => setSelectedStaff(e.value)}
                onSelect={selectStaff}
                panelStyle={{ width: "256px" }}
              />
              <Tooltip target=".activeStaffHeaderIconTip">
                <div>
                  Searches are limited to a minimum of 3 characters and 100
                  results.
                  <br />
                  Please refine your search if you do not see the staff member
                  you are looking for.
                </div>
              </Tooltip>
              <i
                className={classNames(
                  "pi pi-info-circle",
                  styles.activeStaffHeaderIcon,
                  "activeStaffHeaderIconTip",
                )}
                data-pr-position="right"
              />
            </div>
          </>
        )}
    </div>
  )
}
